import { PermissionPreset } from '@capturi/core'
import { Trans, t } from '@lingui/macro'
import { ReactElement, useMemo } from 'react'
import { IconType } from 'react-icons'
import {
  MdEdit,
  MdLockOutline,
  MdVisibility,
  MdVisibilityOff,
} from 'react-icons/md'

export type Option = {
  value: PermissionPreset
  label: string
  category: 'shared' | 'notShared'
  icon: IconType
  description: ReactElement
}

export const getSharingOptions = (): Option[] => {
  return [
    {
      icon: MdVisibility,
      value: 'EveryoneCanView',
      label: t`Everyone can Edit, Submit and View`,
      category: 'shared',
      description: (
        <Trans>
          Edit template: Everyone with permission to 'Edit QA templates'
          <br />
          Submit reviews: Everyone with general 'QA permission' enabled
          <br />
          View submitted reviews: Everyone
        </Trans>
      ),
    },
    {
      icon: MdVisibilityOff,
      value: 'Private',
      label: t`Private`,
      category: 'notShared',
      description: (
        <Trans>
          Edit template: Only you
          <br />
          Submit reviews: Only you
          <br />
          View submitted reviews: Only you
        </Trans>
      ),
    },
    {
      icon: MdLockOutline,
      value: 'Locked',
      label: t`Only you can edit`,
      category: 'shared',
      description: (
        <Trans>
          Edit template: Only you
          <br />
          Submit reviews: Everyone with general 'QA permission' enabled
          <br />
          View submitted reviews: Everyone
        </Trans>
      ),
    },
    {
      icon: MdEdit,
      value: 'Custom',
      label: t`Custom sharing rules`,
      category: 'shared',
      description: (
        <Trans>
          Customized rules for who can Submit reviews, View reviews and Edit
          template
        </Trans>
      ),
    },
  ]
}

export const useSharingOptions = () => {
  return useMemo(() => {
    const sharingOptions = getSharingOptions()
    const sharingOptionsMap = sharingOptions.reduce<Record<string, Option>>(
      (acc, option) => {
        acc[option.value] = option
        return acc
      },
      {},
    )

    const getIconForPermissionPreset = (
      permissionPreset: PermissionPreset,
    ): IconType | undefined => {
      return sharingOptionsMap[permissionPreset]?.icon
    }

    return {
      sharingOptions,
      getIconForPermissionPreset,
    }
  }, [])
}
